import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  
  Collapse,
  
  Paper,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import { API_URL } from "../config";

// MUI Icons
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SchoolIcon from "@mui/icons-material/School";
import BlockIcon from "@mui/icons-material/Block";
import VideoIcon from "@mui/icons-material/VideoLibrary";
import PublicIcon from "@mui/icons-material/Public";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PersonIcon from "@mui/icons-material/Person";


// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    secondary: { main: "#C4922A", light: "#FEF3C7" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const countUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 45%, #E8EDF8 100%)",
  padding: "32px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -160,
    right: -160,
    width: 560,
    height: 560,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "fixed",
    bottom: -120,
    left: -120,
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(196,146,42,0.05) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 1100, margin: "0 auto" });

const HeroSection = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  background: "linear-gradient(135deg, #1E3A8A 0%, #2563EB 50%, #3B82F6 100%)",
  padding: "36px 40px",
  marginBottom: 28,
  position: "relative",
  overflow: "hidden",
  animation: `${fadeUp} 0.5s ease both`,
  boxShadow: "0 8px 32px rgba(37,99,235,0.28), 0 2px 8px rgba(0,0,0,0.1)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -60,
    right: -60,
    width: 280,
    height: 280,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -80,
    left: 200,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.04)",
    pointerEvents: "none",
  },
}));

const StatCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay" && p !== "accentcolor",
})(({ theme, delay, accentcolor }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(accentcolor || theme.palette.primary.main, 0.12)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.04)",
  overflow: "hidden",
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: `0 8px 24px ${alpha(
      accentcolor || theme.palette.primary.main,
      0.14
    )}, 0 12px 32px rgba(0,0,0,0.07)`,
    borderColor: alpha(accentcolor || theme.palette.primary.main, 0.28),
  },
}));

const StatIconBox = styled(Box)(({ accentcolor }) => ({
  width: 52,
  height: 52,
  borderRadius: 14,
  flexShrink: 0,
  background: alpha(accentcolor || "#2563EB", 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.22s ease",
}));

const StatNumber = styled(Typography)(() => ({
  fontFamily: "'DM Serif Display', serif",
  fontSize: 34,
  fontWeight: 400,
  lineHeight: 1,
  letterSpacing: "-1px",
  animation: `${countUp} 0.5s ease both`,
}));

const ToggleChip = styled(Box, {
  shouldForwardProp: (p) => p !== "open",
})(({ theme, open }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 10px",
  borderRadius: 20,
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 700,
  fontFamily: "'Sora', sans-serif",
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  border: `1px solid ${
    open ? alpha(theme.palette.primary.main, 0.3) : alpha("#000", 0.1)
  }`,
  background: open
    ? alpha(theme.palette.primary.main, 0.07)
    : alpha("#000", 0.03),
  color: open ? theme.palette.primary.main : theme.palette.text.disabled,
  transition: "all 0.18s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.1),
    borderColor: alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.primary.main,
  },
}));

const ActionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay" && p !== "accentcolor",
})(({ theme, delay, accentcolor }) => ({
  borderRadius: 14,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.03)",
  padding: "18px 20px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  cursor: "pointer",
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "all 0.22s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 6px 20px ${alpha(
      accentcolor || "#2563EB",
      0.12
    )}, 0 8px 24px rgba(0,0,0,0.06)`,
    borderColor: alpha(accentcolor || "#2563EB", 0.2),
    "& .action-icon-box": {
      background: accentcolor || "#2563EB",
      "& svg": { color: "#fff !important" },
    },
    "& .action-arrow": { transform: "translateX(4px)", opacity: 1 },
  },
}));

const ActionIconBox = styled(Box)(({ accentcolor }) => ({
  width: 44,
  height: 44,
  borderRadius: 12,
  flexShrink: 0,
  background: alpha(accentcolor || "#2563EB", 0.09),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.22s ease",
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const Spinner = styled(Box)(() => ({
  width: 36,
  height: 36,
  border: "3px solid rgba(37,99,235,0.15)",
  borderTop: "3px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
}));

// ─── Stat card colors ─────────────────────────────────────────────────────────
const STAT_META = [
  {
    key: "total",
    title: "Total Students",
    countKey: "total_users",
    listKey: ["inst_users_list", "non_inst_users_list"],
    icon: PeopleAltIcon,
    color: "#2563EB",
  },
  {
    key: "inst",
    title: "Institution Students",
    countKey: "inst_users_count",
    listKey: ["inst_users_list"],
    icon: AccountBalanceIcon,
    color: "#7C3AED",
  },
  {
    key: "non_inst",
    title: "Regular Students",
    countKey: "non_inst_users_count",
    listKey: ["non_inst_users_list"],
    icon: SchoolIcon,
    color: "#059669",
  },
  {
    key: "inst_no_plan",
    title: "No Active Plan",
    countKey: "inst_no_active_plan_count",
    listKey: ["inst_no_active_plan_list"],
    icon: BlockIcon,
    color: "#DC2626",
  },
];

const ACTION_META = [
  {
    label: "Manage Exams",
    icon: PublicIcon,
    path: "/admin-exam-country",
    color: "#2563EB",
  },
  {
    label: "Manage Students",
    icon: ManageAccountsIcon,
    path: "/admin-student-country",
    color: "#7C3AED",
  },

  {
    label: "Promo Codes",
    icon: LocalOfferIcon,
    path: "/admin-promo-country",
    color: "#C4922A",
  },
  {
    label: "Locations",
    icon: LocationOnIcon,
    path: "/admin-location-count",
    color: "#059669",
  },
  {
    label: "Countries",
    icon: PublicIcon,
    path: "/admin-country",
    color: "#0891B2",
  },
  {
    label: "Coins & Rewards",
    icon: EmojiEventsIcon,
    path: "/admin-gold-country",
    color: "#D97706",
  },
  {
    label: "Manage Videos",
    icon: VideoIcon,
    path: "/admin-video-country",
    color: "#c720b1",
  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    const tokenString = localStorage.getItem("authToken");
    if (!tokenString) {
      navigate("/login");
      return;
    }
    const token = JSON.parse(tokenString);
    axios
      .get(`${API_URL}/admin/user-count/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      })
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error(err);
        if (err.response?.status === 401) navigate("/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const toggleSection = (section) =>
    setOpenSection(openSection === section ? null : section);

  const getList = (listKeys, data) =>
    listKeys.flatMap((k) => (Array.isArray(data[k]) ? data[k] : []));

  // ── Loading screen ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <link
          href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
          rel="stylesheet"
        />
        <PageWrapper>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "80vh",
              gap: 2,
            }}
          >
            <Spinner />
            <Typography
              sx={{
                fontSize: 13,
                color: "text.disabled",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Loading dashboard…
            </Typography>
          </Box>
        </PageWrapper>
      </ThemeProvider>
    );
  }

  const now = new Date();
  const greeting =
    now.getHours() < 12
      ? "Good morning"
      : now.getHours() < 17
      ? "Good afternoon"
      : "Good evening";

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Hero Banner ── */}
          <HeroSection>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.6)",
                      fontFamily: "'Sora', sans-serif",
                      mb: 0.5,
                    }}
                  >
                    {greeting}
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: 26, sm: 34 },
                      color: "#fff",
                      lineHeight: 1.1,
                      mb: 0.8,
                    }}
                  >
                    Admin Dashboard
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.65)",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  >
                    Manage your platform with ease
                  </Typography>
                </Box>

                {/* Total count hero stat */}
                <Box
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 16,
                    border: "1px solid rgba(255,255,255,0.15)",
                    padding: "16px 24px",
                    textAlign: "center",
                    backdropFilter: "blur(10px)",
                    animation: `${float} 4s ease-in-out infinite`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      color: "rgba(255,255,255,0.6)",
                      fontFamily: "'Sora', sans-serif",
                      mb: 0.5,
                    }}
                  >
                    Total Students
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: 44,
                      color: "#fff",
                      lineHeight: 1,
                      letterSpacing: "-2px",
                    }}
                  >
                    {data.total_users ?? 0}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      mt: 0.8,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#4ADE80",
                        animation: `${shimmer} 2s infinite`,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.6)",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      Live
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Mini stat pills */}
              <Box sx={{ display: "flex", gap: 1.5, mt: 3, flexWrap: "wrap" }}>
                {[
                  {
                    label: "Institution",
                    val: data.inst_users_count ?? 0,
                    color: "#A78BFA",
                  },
                  {
                    label: "Regular",
                    val: data.non_inst_users_count ?? 0,
                    color: "#34D399",
                  },
                  {
                    label: "No Plan",
                    val: data.inst_no_active_plan_count ?? 0,
                    color: "#F87171",
                  },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 20,
                      px: 1.8,
                      py: 0.6,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: s.color,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      {s.val}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.6)",
                        fontFamily: "'Sora', sans-serif",
                      }}
                    >
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </HeroSection>

          {/* ── Stat Cards ── */}
          <Box sx={{ mb: 3 }}>
            <SectionLabel>User Overview</SectionLabel>
            <Grid container spacing={2.5}>
              {STAT_META.map((meta, i) => {
                const Icon = meta.icon;
                const list = getList(meta.listKey, data);
                const count =
                  i === 0 ? data.total_users ?? 0 : data[meta.countKey] ?? 0;
                const isOpen = openSection === meta.key;

                return (
                  <Grid item xs={12} sm={6} lg={3} key={meta.key}>
                    <StatCard accentcolor={meta.color} delay={i * 70}>
                      <Box sx={{ p: "20px 20px 16px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <StatIconBox accentcolor={meta.color}>
                            <Icon sx={{ fontSize: 22, color: meta.color }} />
                          </StatIconBox>
                          <ToggleChip
                            open={isOpen ? 1 : 0}
                            onClick={() => toggleSection(meta.key)}
                          >
                            {isOpen ? (
                              <ExpandLessIcon sx={{ fontSize: 13 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 13 }} />
                            )}
                            {isOpen ? "Hide" : "List"}
                          </ToggleChip>
                        </Box>

                        <StatNumber sx={{ color: meta.color }}>
                          {count}
                        </StatNumber>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "text.secondary",
                            fontFamily: "'Sora', sans-serif",
                            mt: 0.6,
                            fontWeight: 600,
                          }}
                        >
                          {meta.title}
                        </Typography>
                      </Box>

                      {/* Collapsible list */}
                      <Collapse in={isOpen}>
                        <Box
                          sx={{
                            borderTop: `1px solid ${alpha(meta.color, 0.1)}`,
                            background: alpha(meta.color, 0.02),
                            maxHeight: 220,
                            overflowY: "auto",
                            px: 2,
                            py: 1,
                          }}
                        >
                          {list.length > 0 ? (
                            list.map((user, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                  py: 1,
                                  borderBottom:
                                    idx < list.length - 1
                                      ? `1px solid ${alpha("#000", 0.05)}`
                                      : "none",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "8px",
                                    flexShrink: 0,
                                    background: alpha(meta.color, 0.1),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <PersonIcon
                                    sx={{ fontSize: 14, color: meta.color }}
                                  />
                                </Box>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: "text.secondary",
                                    fontFamily: "'Sora', sans-serif",
                                    fontWeight: 500,
                                  }}
                                >
                                  {user.name ||
                                    user.user__username ||
                                    user.username ||
                                    "Unknown User"}
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "text.disabled",
                                fontFamily: "'Sora', sans-serif",
                                py: 1.5,
                                textAlign: "center",
                              }}
                            >
                              No users in this category
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </StatCard>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* ── Quick Actions ── */}
          <Box>
            <SectionLabel>Quick Actions</SectionLabel>
            <Grid container spacing={2}>
              {ACTION_META.map((action, i) => {
                const Icon = action.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={action.label}>
                    <ActionCard
                      accentcolor={action.color}
                      delay={i * 60}
                      onClick={() => navigate(action.path)}
                    >
                      <ActionIconBox
                        className="action-icon-box"
                        accentcolor={action.color}
                      >
                        <Icon
                          sx={{
                            fontSize: 20,
                            color: action.color,
                            transition: "color 0.22s ease",
                          }}
                        />
                      </ActionIconBox>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "text.primary",
                            fontFamily: "'Sora', sans-serif",
                            lineHeight: 1.2,
                          }}
                        >
                          {action.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "text.disabled",
                            fontFamily: "'Sora', sans-serif",
                            mt: 0.2,
                          }}
                        >
                          Tap to manage
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          background: alpha(action.color, 0.07),
                          border: `1px solid ${alpha(action.color, 0.15)}`,
                          borderRadius: 2,
                          px: 1.2,
                          py: 0.6,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: action.color,
                            fontFamily: "'Sora', sans-serif",
                          }}
                        >
                          Go
                        </Typography>
                        <ArrowForwardIosIcon
                          className="action-arrow"
                          sx={{
                            fontSize: 10,
                            color: action.color,
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </Box>
                    </ActionCard>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default DashboardPage;
